import type * as CSS from 'csstype';
import React, { MouseEvent, RefObject } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled, { css, StyleSheetManager } from 'styled-components';

import { useButtonHotkey } from '../../hooks/use-button-hotkey';
import { useFloatingPosition } from '../../hooks/use-floating-position';
import { reset } from '../../utils/styled-reset';
import { Icon } from '../icon/icon';
import { Stack } from '../stack/stack';
import { dialogDock, dialogZIndex } from './dialog-dock';
import { OwnWindowContext } from './own-window';

/**
 * Provides the FloatLayer element of the nearest parent Dialog.
 * Floating elements (dropdowns, menus, etc.) inside a Dialog should portal
 * into this element so they remain within the dialog's stacking context,
 * appearing above the dialog content but below any subsequently opened dialog.
 */
export const DialogPortalContext = React.createContext<HTMLElement | null>(null);

type Position =
  | 'center'
  | {
      top: number;
      left: number;
    };

type Props = {
  /**
   * @default false
   */
  disableDimmer?: boolean;
  /**
   * @default false
   */
  closeOnDimmerClick?: boolean;
  onClose: () => Promise<void> | void;
  /**
   * @default center
   */
  position?: Position;
  maxWidth?: number | CSS.Properties['width'];
  width?: number | CSS.Properties['width'];
  height?: number | string;
  /**
   * Determines max height behavrious
   * @default true
   */
  maxHeight?: boolean | 'expand';
  contentPadding?: boolean;
  closeIcon?: boolean;
  useGoogleTranslate?: boolean;
  closeButtonRef?: RefObject<HTMLButtonElement>;
  confirmButtonRef?: RefObject<HTMLButtonElement>;
  headerIcons?: React.ReactNode | React.ReactNode[];
  /**
   * Whether the dialog can be moved by dragging its header, and docked below other movable dialogs,
   * see dialogDock. It can also be opened in a window of its own, from an icon in its header, to be
   * moved outside the browser window. It has no dimmer.
   * @default false
   */
  movable?: boolean;
  /**
   * Whether a movable dialog is docked by itself below the last one that was, when it opens, e.g.
   * the dialogs that open beside the asset menu. Otherwise it opens at its position.
   * @default false
   */
  autoDock?: boolean;
  /**
   * Whether keyboard focus moves into the dialog when it opens, and back to where it was when it
   * closes, e.g. for a dialog without fields that is opened from an icon. There is no focus trap.
   * @default false
   */
  focusOnOpen?: boolean;
  /**
   * Whether the content and footer of the dialog can be collapsed, from an icon in its header, so
   * that only the header is shown.
   * @default false
   */
  collapsible?: boolean;
  /**
   * Whether a collapsible dialog is collapsed when it opens.
   * @default false
   */
  defaultCollapsed?: boolean;
  /**
   * Whether a collapsible dialog is collapsed, when it is controlled by the parent instead of
   * defaultCollapsed, e.g. to expand it from outside. It is then collapsed and expanded from its
   * header through onCollapsedChanged.
   */
  collapsed?: boolean;
  /**
   * Whether the content is a column that fills the dialog, whose parts scroll by themselves, e.g.
   * a list with details below it, instead of the content scrolling as a whole. The dialog then also
   * shrinks to fit a window of its own, instead of the window scrolling.
   * @default false
   */
  fillContent?: boolean;
  /**
   * Called when a collapsible dialog is collapsed or expanded from the icon in its header.
   */
  onCollapsedChanged?: (collapsed: boolean) => void;
  children: {
    header: React.ReactNode;
    content: React.ReactNode;
    /**
     * Content in footer can be placed to the left and to the right if specified. Otherwise it
     * renders the component as is. If left and right properties are used, content is per default
     * stacked horizontally. If you want to suppress this, wrap the content with another element
     * (<div>...</div> for instance).
     */
    footer?: { left?: React.ReactNode; right?: React.ReactNode } | React.ReactNode;
  };
  context?: (children: React.ReactNode) => React.ReactNode;
  /**
   * When set, the dialog is portaled into this element instead of document.body.
   * Use to keep the dialog inside a parent's DOM hierarchy (e.g. for stacking order).
   */
  portalTarget?: HTMLElement | null;
};

/**
 * Brings a dialog that is interacted with in front of the other dialogs, which are stacked in the
 * order their wrappers are in the body, see wrapperStyle, and the groups of dialogDock. The wrappers
 * after its own are moved before it: moving its own on pointer down would cancel the click on it.
 */
const bringToFront = (wrapper: Element | null | undefined, e: React.PointerEvent) => {
  // The events of a dialog opened from this one, which is portaled elsewhere, also come here.
  if (!wrapper || wrapper.parentElement !== document.body || !wrapper.contains(e.target as Node)) {
    return;
  }

  let next = wrapper.nextElementSibling;
  while (next) {
    const sibling = next;
    next = next.nextElementSibling;

    if (sibling.hasAttribute('data-dialog-wrapper')) {
      document.body.insertBefore(sibling, wrapper);
    }
  }
};

/**
 * Docks a movable dialog with the others, see Props.movable and dialogDock. Returns the element it
 * is rendered into, which the dock moves between groups and windows, and where it is in the dock.
 */
const useDock = (
  movable: boolean,
  options: { autoDock: boolean; position: Position; onClose: Props['onClose'] },
) => {
  const id = React.useId();
  const [host] = React.useState(() => {
    const element = document.createElement('div');
    // Its dialog is stacked in the container of its group.
    element.style.display = 'contents';
    return element;
  });
  const onClose = React.useRef(options.onClose);
  onClose.current = options.onClose;
  const opening = React.useRef(options);

  React.useLayoutEffect(() => {
    if (!movable) {
      return;
    }

    // Placed where it opens, and not moved there again.
    const { autoDock, position } = opening.current;
    dialogDock.add(
      { id, host, onClose: () => void onClose.current() },
      { autoDock, position: typeof position === 'string' ? 'center' : position },
    );

    return () => dialogDock.remove(id);
  }, [movable, id, host]);

  const state = React.useSyncExternalStore(dialogDock.subscribe, () => dialogDock.getState(id));
  return { id, host, state };
};

const Dialog = ({
  disableDimmer = false,
  closeOnDimmerClick = false,
  maxHeight = true,
  position = 'center',
  context = (children) => <>{children}</>,
  portalTarget: portalTargetProp,
  movable = false,
  autoDock = false,
  focusOnOpen = false,
  collapsible = false,
  defaultCollapsed = false,
  collapsed: collapsedProp,
  fillContent = false,
  onCollapsedChanged,
  ...props
}: Props) => {
  const { t } = useTranslation();
  // The window of its own the part that opens the dialog is in, e.g. the right menu
  const partWindow = React.useContext(OwnWindowContext);
  const [collapsedState, setCollapsed] = React.useState(collapsible && defaultCollapsed);
  const collapsed = collapsible && (collapsedProp ?? collapsedState);
  const [floatLayerEl, setFloatLayerEl] = React.useState<HTMLDivElement | null>(null);
  const { ref, floatingStyles, floatingProps } = useFloatingPosition({
    followMousePointer: false,
    middleware: 'shift',
    offset: {
      mousePointer: 10,
      viewportEdge: 5,
    },
    startPos:
      typeof position === 'string' || !position
        ? undefined
        : {
            x: position.left,
            y: position.top,
          },
    strategy: 'fixed',
  });

  const dock = useDock(movable, { autoDock, position, onClose: props.onClose });

  // Its window is fitted to it when it is expanded or collapsed by the parent, as from its header.
  const lastCollapsedProp = React.useRef(collapsedProp);
  React.useLayoutEffect(() => {
    if (lastCollapsedProp.current !== collapsedProp) {
      lastCollapsedProp.current = collapsedProp;
      dialogDock.fitWindowOf(dock.id);
    }
  }, [collapsedProp, dock.id]);

  // The window of its own the dialog is in, see dialogDock.popOut.
  const dockWindow = dock.state.window;
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const headerId = React.useId();

  React.useEffect(() => {
    if (!focusOnOpen) {
      return;
    }

    const previous = document.activeElement as HTMLElement | null;
    (movable ? dock.host : wrapperRef.current)
      ?.querySelector<HTMLElement>('[role="dialog"]')
      ?.focus();

    return () => {
      if (previous?.isConnected) {
        previous.focus();
      }
    };
  }, [focusOnOpen, movable, dock.host]);

  useButtonHotkey(props.closeButtonRef, 'Escape', { bubble: true });
  useButtonHotkey(props.confirmButtonRef, 'Enter');

  const onDimmerClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (closeOnDimmerClick) {
      (props.onClose as () => void)();
    }
  };

  const renderFooter = (footer: NonNullable<Props['children']['footer']>) => {
    const leftStack =
      typeof footer === 'object' && 'left' in footer && footer.left ? (
        <Stack direction="row" spacing={0.5}>
          {footer.left}
        </Stack>
      ) : undefined;
    const rightStack =
      typeof footer === 'object' && 'right' in footer && footer.right ? (
        <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
          {footer.right}
        </Stack>
      ) : undefined;

    return leftStack && rightStack ? (
      <Stack direction="row" justifyContent="space-between" spacing={0.5}>
        {leftStack}
        {rightStack}
      </Stack>
    ) : (
      leftStack ?? rightStack ?? (footer as React.ReactNode)
    );
  };

  const toggleCollapsed = () => {
    // Right away, so that its window is fitted to it as it is then.
    ReactDOM.flushSync(() => {
      setCollapsed(!collapsed);
      onCollapsedChanged?.(!collapsed);
    });
    dialogDock.fitWindowOf(dock.id);
  };

  const onHeaderPointerDown = (e: React.PointerEvent) => {
    // The icons of the header, such as the close icon, are clicked, not dragged.
    if (e.button !== 0 || (e.target as Element).closest('svg, button')) {
      return;
    }

    e.preventDefault();
    dialogDock.drag(dock.id, e.nativeEvent);
  };

  const isCentered = typeof position === 'string';
  // A movable dialog is placed by its group, see dialogDock.
  const isPlaced = !isCentered && !movable;
  const isLast = dock.state.index === dock.state.count - 1;

  const component = context(
    <DialogPortalContext.Provider value={floatLayerEl}>
      {!disableDimmer && !movable && <Dimmer onClick={onDimmerClick} />}
      <Component
        aria-labelledby={headerId}
        role="dialog"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onPointerDownCapture={(e) =>
          bringToFront(movable ? dock.host.parentElement : wrapperRef.current, e)
        }
        {...props}
        className={props.useGoogleTranslate ? undefined : 'notranslate'}
        data-testid="dialog"
        maxHeight={maxHeight}
        position={position}
        // Apply Floating-UI specific things only when dialog is explicitly set to a fixed position.
        ref={isPlaced ? ref : undefined}
        style={
          movable
            ? {
                // In the container of its group, see dialogDock.
                position: 'relative',
                top: 'auto',
                left: 'auto',
                transform: 'none',
                // As wide as the widest dialog of its group.
                minWidth: '100%',
                width:
                  typeof props.width === 'number' ? `min(${props.width}px, 90vw)` : props.width,
                maxWidth:
                  typeof props.maxWidth === 'number'
                    ? `min(${props.maxWidth}px, 90vw)`
                    : props.maxWidth,
                borderRadius: 0,
                boxShadow: 'none',
                borderTop: dock.state.index > 0 ? '1px solid #ccc' : undefined,
                // In a window, the last one fills it where it is higher than the dialogs.
                ...(dockWindow &&
                  (fillContent
                    ? { maxHeight: 'none', minHeight: 0, flex: isLast ? '1 1 auto' : '0 1 auto' }
                    : { maxHeight: 'none', flex: isLast ? '1 0 auto' : 'none' })),
              }
            : isPlaced
              ? floatingStyles
              : undefined
        }
        {...(isPlaced ? floatingProps : [])}
      >
        <Header
          $collapsible={collapsible}
          $compact={movable}
          $fill={!!dockWindow && collapsed && isLast}
          $movable={movable && !dockWindow}
          // Clicked anywhere, but on its icons, it collapses or expands the dialog.
          onClick={(e) => {
            if (
              collapsible &&
              !(movable && !dockWindow && dialogDock.wasDragged()) &&
              !(e.target as Element).closest('svg, button')
            ) {
              toggleCollapsed();
            }
          }}
          onPointerDown={movable && !dockWindow ? onHeaderPointerDown : undefined}
        >
          <Stack alignItems="baseline" direction="row" justifyContent="space-between" spacing={1}>
            <span
              data-testid="dialog-header"
              id={headerId}
              title={typeof props.children.header === 'string' ? props.children.header : undefined}
            >
              {props.children.header}
            </span>
            <Stack alignItems="baseline" direction="row" justifyContent="right" spacing={'1em'}>
              {props.headerIcons}
              {dockWindow && (
                <Icon
                  icon="square-arrow-down-left"
                  title={t('dialog.popIn', { ns: 'common' })}
                  onClick={() => dialogDock.putBack(dock.id)}
                />
              )}
              {movable && !dockWindow && (
                <Icon
                  icon="arrow-up-right-from-square"
                  title={t('dialog.popOut', { ns: 'common' })}
                  // Collapsed or not, as it is: a window higher than its dialogs is filled.
                  onClick={() => dialogDock.popOut(dock.id)}
                />
              )}
              {collapsible && (
                <Icon
                  icon={collapsed ? 'chevron-down' : 'chevron-up'}
                  title={
                    collapsed ? t('expand', { ns: 'common' }) : t('collapse', { ns: 'common' })
                  }
                  onClick={toggleCollapsed}
                />
              )}
              {/* In a window of its own it is closed with the close button of the window. */}
              {props.closeIcon && !dockWindow && <Icon icon="xmark" onClick={props.onClose} />}
            </Stack>
          </Stack>
        </Header>
        {/* Hidden rather than removed when collapsed, so that its state is kept. */}
        <Content $fill={fillContent} hidden={collapsed} padding={props.contentPadding ?? true}>
          {props.children.content}
        </Content>
        {props.children.footer && (
          <Footer hidden={collapsed}>{renderFooter(props.children.footer)}</Footer>
        )}
      </Component>
      {/* FloatLayer: a fixed full-viewport layer above the dialog box, within this dialog's
          stacking context. Floating elements (dropdowns etc.) inside the dialog portal here
          so they are never clipped by overflow:hidden and always above the dialog content,
          but automatically below any subsequently opened dialog (DOM order). */}
      <FloatLayer ref={setFloatLayerEl} />
    </DialogPortalContext.Provider>,
  );

  if (movable) {
    return ReactDOM.createPortal(
      // Its styles are added to the window it is in, see dialogDock.popOut.
      <StyleSheetManager target={dockWindow?.document.head}>
        <OwnWindowContext.Provider value={dockWindow}>{component}</OwnWindowContext.Provider>
      </StyleSheetManager>,
      dock.host,
    );
  }

  const target = portalTargetProp ?? document.body;
  // When portaling to document.body:
  //   - position:fixed + z-index:dialogZIndex ensures the dialog sits above all viewer UI
  //     (asset menu, toolbar, etc.) without relying on those elements' z-indices.
  //   - isolation:isolate turns the wrapper into its own stacking context so that internal
  //     z-indices (Dimmer, Component, FloatLayer) are local and don't compete globally.
  //   - pointer-events:none lets clicks pass through empty areas of the wrapper to the canvas;
  //     Dimmer and Component restore pointer events for their own areas.
  //   - With the same z-index on every dialog wrapper, DOM order (later portal = later in body)
  //     determines which dialog sits on top — no per-dialog z-index management needed.
  const wrapperStyle: React.CSSProperties =
    target === document.body
      ? {
          position: 'fixed',
          inset: 0,
          zIndex: dialogZIndex,
          isolation: 'isolate',
          pointerEvents: 'none',
        }
      : { pointerEvents: 'auto', height: '100%', isolation: 'isolate' };
  const wrapper = (
    <div data-dialog-wrapper={true} ref={wrapperRef} style={wrapperStyle}>
      {component}
    </div>
  );
  return ReactDOM.createPortal(
    // Opened from a part in a window of its own, its styles would be added there, not to the page
    // it is in
    partWindow && target.ownerDocument === document ? (
      <StyleSheetManager target={document.head}>
        <OwnWindowContext.Provider value={undefined}>{wrapper}</OwnWindowContext.Provider>
      </StyleSheetManager>
    ) : (
      wrapper
    ),
    target,
  );
};

const Dimmer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: ${dialogZIndex};
  pointer-events: auto;
`;

const Component = styled.div<
  Pick<Props, 'width' | 'maxWidth' | 'height' | 'maxHeight'> & Required<Pick<Props, 'position'>>
>`
  ${reset}
  --padding: 1em;

  position: fixed;
  font-size: 14px;
  background-color: #fff;
  box-shadow: 2px 2px 8px 0 #00000060;
  border-radius: 5px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: ${dialogZIndex};
  pointer-events: auto;

  ${({ width }) =>
    typeof width === 'number'
      ? css`
          width: clamp(0px, ${width}px, 90%);
        `
      : css`
          width: ${width};
        `}

  ${({ maxWidth }) =>
    typeof maxWidth === 'number'
      ? css`
          max-width: clamp(0px, ${maxWidth}px, 90%);
        `
      : css`
          max-width: ${maxWidth};
        `}

  ${({ height, maxHeight }) =>
    maxHeight === 'expand'
      ? css`
          max-height: calc(100% - 2em);
        `
      : height
        ? css`
            height: ${typeof height === 'string' ? height : `${height}px`};
          `
        : maxHeight
          ? css`
              max-height: 60vh;
            `
          : undefined}

  ${({ position }) =>
    position === 'center'
      ? css`
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        `
      : undefined}

  // Where a dragged dialog is docked if it is dropped, see dialogDock.drag.
  &[data-dock-target]::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
    background-color: ${({ theme }) => theme.color.brand.dark};
  }
`;

const Header = styled.header<{
  $movable?: boolean;
  $compact: boolean;
  $collapsible: boolean;
  $fill: boolean;
}>`
  padding: var(--padding);
  background-color: #eee;

  // A collapsed dialog in a window of its own, which the browser doesn't shrink below a minimum
  // height: the header fills the rest of it, with its title where it is.
  ${({ $fill }) =>
    $fill &&
    css`
      flex: 1;
    `}

  // A thinner title bar for the dialogs that are moved around, see Props.movable.
  ${({ $compact }) =>
    $compact &&
    css`
      padding: 0.5em var(--padding);
    `}

  ${({ $collapsible }) =>
    $collapsible &&
    css`
      cursor: pointer;
      user-select: none;
    `}

  ${({ $movable }) =>
    $movable &&
    css`
      cursor: move;
      user-select: none;
      touch-action: none;
    `}

  span {
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    // Shrinks in the row with the icons, so that a long title gets an ellipsis.
    min-width: 0;
  }
`;

const Content = styled.div<{ padding: boolean; $fill: boolean }>`
  // Margin top instead of padding top in order to be able
  // to place components inside that has sticky with top:0,
  // so overflowed content in component does not overflow
  // outside (above) the sticky element.
  //
  // However, this leaves a space above the scrollbar with
  // height equal to margin-top.
  //
  // todo: Check if we can use padding for all sides and
  // solve this problem using another method.
  ${(props) =>
    props.padding &&
    css`
      margin-top: var(--padding);
      padding: 0 var(--padding) var(--padding) var(--padding);
    `}

  flex: 1;
  overflow-y: auto;

  ${(props) =>
    props.$fill &&
    css`
      display: flex;
      flex-direction: column;
      min-height: 0;
      overflow: hidden;
    `}

  &[hidden] {
    display: none;
  }
`;

const Footer = styled.footer`
  padding: var(--padding);
  background-color: #eee;

  &[hidden] {
    display: none;
  }
`;

/**
 * Full-viewport fixed layer that sits above the dialog box within the dialog's own
 * stacking context. Floating elements (DropdownButton, etc.) portal here when rendered
 * inside a dialog, so they appear above the dialog content without escaping to document.body.
 * pointer-events: none so that clicks on empty areas pass through to the dialog/dimmer;
 * floating children must set pointer-events: auto themselves.
 */
const FloatLayer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: ${dialogZIndex + 1};
`;

export { Dialog, dialogZIndex };
